import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SectionGlow from '@/components/common/SectionGlow';
import { TileImage, mediaGuard } from '@/components/common/Skeleton';
import axios from 'axios';
import { Lock, Trophy, Star, PartyPopper, Play, FileText } from 'lucide-react';
import { usePlatformAuth } from '@/contexts/PlatformAuthContext';
import { trackClick } from '@/lib/track';
import { useJoinPrice } from '@/hooks/useJoinPrice';
import { CopyWithCoinPrice, COIN_PRICE_SLOT } from '@/components/common/CoinPrice';
import { SButton } from '@/components/common/SButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KNOWN_TYPES = ['text', 'poll', 'milestone', 'spotlight', 'attachment', 'welcome', 'premium_upgrade'];

const timeAgo = (iso, t) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return t('home.community.time.just_now');
  if (s < 3600) return t('home.community.time.m_ago', { n: Math.floor(s / 60) });
  if (s < 86400) return t('home.community.time.h_ago', { n: Math.floor(s / 3600) });
  return t('home.community.time.d_ago', { n: Math.floor(s / 86400) });
};

const priceLabel = (p) => (p == null ? '' : Number.isInteger(p) ? `$${p}` : `$${p.toFixed(2)}`);

// Read-only post card for the public homepage preview (no interactions/auth).
const PreviewPost = ({ post }) => {
  const { t } = useTranslation();
  const Icon = { milestone: Trophy, premium_upgrade: Star, welcome: PartyPopper }[post.type];
  const author = post.author || {};
  const initial = (author.name || 'S').charAt(0).toUpperCase();
  const att = post.attachment;
  const typeLabel = KNOWN_TYPES.includes(post.type) ? t(`home.community.type.${post.type}`) : t('home.community.type.text');
  return (
    <div
      data-testid={`home-preview-post-${post.id}`}
      className="rounded-2xl p-4"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}
    >
      <div className="flex items-center gap-2.5 mb-2">
        <div className="relative w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold overflow-hidden" style={{ background: 'var(--accent-primary)', color: '#fff' }}>
          {author.avatar ? <TileImage src={author.avatar} className="w-full h-full object-cover" /> : initial}
        </div>
        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{author.name || 'SAMTWO'}</span>
        {author.is_artist && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'var(--accent-primary)', color: '#fff' }}>{t('home.community.artist_badge')}</span>}
      </div>
      <div className="flex items-center justify-between mb-1">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>
          {Icon && <Icon size={12} style={{ color: 'var(--accent-primary)' }} />}
          {typeLabel}
        </span>
        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{timeAgo(post.created_at, t)}</span>
      </div>
      {post.body && (
        <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }} data-testid={`home-preview-body-${post.id}`}>{post.body}</p>
      )}

      {post.type === 'poll' && post.poll && (
        <div className="mt-3 space-y-2">
          {post.poll.options.map((o, i) => (
            <div key={i} className="px-3 py-2.5 rounded-xl text-sm font-medium" style={{ border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
              {o.option}
            </div>
          ))}
        </div>
      )}

      {post.type === 'spotlight' && post.spotlight && (
        <div className="mt-3 flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border-color)' }}>
          <div style={{ width: 48, height: 48, borderRadius: 8, overflow: 'hidden', flexShrink: 0, position: 'relative', background: 'var(--bg-surface)' }}>
            {post.spotlight.cover ? <TileImage src={post.spotlight.cover} className="w-full h-full object-cover" /> : <Play size={18} style={{ color: 'var(--text-secondary)', margin: 15 }} />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--accent-primary)' }}>{post.spotlight.kind === 'track' ? t('home.community.kind_track') : t('home.community.kind_video')}</p>
            <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{post.spotlight.title}</p>
          </div>
        </div>
      )}

      {post.type === 'attachment' && att && att.url && (
        att.attachment_type === 'image'
          ? <img src={att.url} alt={att.filename || ''} {...mediaGuard().props} className="mt-3 w-full rounded-xl" style={{ maxHeight: 360, objectFit: 'cover', ...mediaGuard().style }} />
          : (
            <div className="mt-3 flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border-color)' }}>
              <FileText size={20} style={{ color: 'var(--accent-primary)' }} />
              <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{att.filename || t('home.community.attachment_fallback')}</span>
            </div>
          )
      )}
    </div>
  );
};

// Section 5 (01-home.md): 1-2 public Community posts inline. Post 1 full, post 2
// fades toward the bottom, then a locked subscribe CTA. Non-subscribers only.
const HomeCommunityPreview = () => {
  const { t } = useTranslation();
  const { everSubscribed } = usePlatformAuth();
  const navigate = useNavigate();
  const { joinCoins } = useJoinPrice();
  const [posts, setPosts] = useState(undefined); // undefined=loading
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    axios.get(`${API}/home/community-preview`).then((r) => setPosts(r.data || [])).catch(() => setPosts([]));
    axios.get(`${API}/settings/pricing`).then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  if (everSubscribed) return null;
  if (posts === undefined) return null;
  if (!posts.length) return null;

  const [first, second] = posts;

  return (
    <section data-testid="home-community-preview" className="relative overflow-hidden py-20 md:py-28" style={{ background: 'var(--bg-primary)' }}>
      <SectionGlow position="15% 15%" strength={10} />
      <div className="relative z-10 max-w-2xl mx-auto px-6 md:px-12">
        <span className="text-overline">{t('home.community.overline')}</span>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-8 space-y-4"
        >
          <PreviewPost post={first} />

          {second && (
            <div className="relative" data-testid="home-preview-faded">
              <div style={{ maxHeight: 220, overflow: 'hidden' }}>
                <PreviewPost post={second} />
              </div>
              {/* Fade toward the bottom */}
              <div
                className="absolute inset-x-0 bottom-0 pointer-events-none"
                style={{ height: 160, background: 'linear-gradient(to bottom, transparent, var(--bg-surface))' }}
              />
            </div>
          )}
        </motion.div>

        <div className="mt-8 text-center flex flex-col items-center gap-4">
          <p className="inline-flex items-center gap-2 text-body-lg font-medium" style={{ color: 'var(--text-primary)' }} data-testid="home-preview-lock-line">
            <Lock size={16} style={{ color: 'var(--accent-primary)' }} />
            {t('home.community.rest_locked')}
          </p>
          <SButton variant="primary" data-testid="home-preview-join" onClick={() => { trackClick('subscribe_cta', { source: 'community_preview' }); navigate('/pricing'); }}>
            <CopyWithCoinPrice template={t('home.community.join_for', { price: COIN_PRICE_SLOT })} coins={joinCoins} size="sm" />
          </SButton>
        </div>
      </div>
    </section>
  );
};

export default HomeCommunityPreview;
